/** PICK A WINNER IN EVERY FIGHT — the fight-card version of Pick your winner.
 *
 *  One booking is the whole card, so the player taps a winner in each bout.
 *  Each fight closes on its own the moment it starts, while the fights later on
 *  the card stay open. Once a fight is over the row shows who won and whether
 *  this player had it right.
 */
import { useState } from 'react';
import { Check, Loader2, Lock, Swords, X } from 'lucide-react';
import { toast } from 'sonner';

const ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';

export const CardPicks = ({ cardId, block, deviceId, t, c, onPicked }) => {
    const [busy, setBusy] = useState('');
    const [picks, setPicks] = useState(() => Object.fromEntries(
        (block?.bouts || []).map((b) => [b.id, b.my_pick || null])));

    if (!block?.enabled || block.mode !== 'card') return null;

    const pick = async (bout, side) => {
        if (bout.locked) return;
        setBusy(`${bout.id}:${side}`);
        try {
            const res = await fetch(`${ORIGIN}/api/goal-game/public/${cardId}/card-pick`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ device_id: deviceId, bout_id: bout.id, side }),
            });
            const data = await res.json();
            if (!res.ok) {
                const reason = data?.detail;
                toast.error(reason === 'sign_in_first' ? t('goalGame.cardPickSignIn')
                    : reason === 'fight_started' ? t('goalGame.cardPickClosed')
                        : t('goalGame.cardPickFailed'));
                return;
            }
            setPicks((old) => ({ ...old, [bout.id]: side }));
            onPicked?.(data);
        } catch (_e) {
            toast.error(t('goalGame.cardPickFailed'));
        } finally { setBusy(''); }
    };

    const accent = c?.accent || '#F59E0B';

    return (
        <div className="mb-4 rounded-2xl border border-white/10 bg-black/20 p-4"
             data-testid="goal-card-picks">
            <div className="mb-3 flex items-center justify-between gap-3">
                <p className="flex items-center gap-2 text-sm font-black uppercase tracking-wide">
                    <Swords size={16} style={{ color: accent }} />
                    {t('goalGame.cardPickTitle')}
                </p>
                <span className="text-xs opacity-70" data-testid="goal-card-picks-score">
                    {t('goalGame.cardPickScore', {
                        right: block.score?.right || 0,
                        decided: block.score?.decided || 0,
                    })}
                </span>
            </div>
            <p className="mb-3 text-xs opacity-60" data-testid="goal-card-picks-points">
                {t('goalGame.cardPickPoints', { points: block.points })}
            </p>

            <ul className="space-y-2">
                {(block.bouts || []).map((bout) => {
                    const mine = picks[bout.id];
                    const done = bout.state === 'FINAL' && bout.winner;
                    const right = done && mine && mine === bout.winner;
                    const wrong = done && mine && mine !== bout.winner;
                    return (
                        <li key={bout.id} className="rounded-xl bg-white/5 p-2"
                            data-testid={`goal-card-bout-${bout.order}`}>
                            <div className="mb-1 flex items-center gap-2 text-[11px] uppercase tracking-wide opacity-55">
                                <span>{t('goalGame.cardFightNo', { n: bout.order })}</span>
                                {bout.state === 'LIVE' && (
                                    <span className="font-bold" style={{ color: accent }}
                                          data-testid={`goal-card-bout-live-${bout.order}`}>
                                        {t('goalGame.cardRoundNow', { round: bout.round || 1 })}
                                        {bout.clock && bout.clock !== '-' ? ` · ${bout.clock}` : ''}
                                    </span>
                                )}
                                {done && <span data-testid={`goal-card-bout-final-${bout.order}`}>
                                    {t('goalGame.cardFightOver')}
                                </span>}
                                {bout.locked && !done && <Lock size={11} />}
                                {right && <Check size={13} className="text-emerald-400" />}
                                {wrong && <X size={13} className="text-rose-400" />}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                {(bout.fighters || []).map((f) => {
                                    const chosen = mine === f.side;
                                    const won = done && bout.winner === f.side;
                                    return (
                                        <button key={f.side} type="button"
                                                disabled={bout.locked || busy === `${bout.id}:${f.side}`}
                                                onClick={() => pick(bout, f.side)}
                                                data-testid={`goal-card-pick-${bout.order}-${f.side}`}
                                                className={`rounded-lg px-2 py-2 text-xs font-bold transition-colors ${
                                                    chosen ? 'text-black' : 'bg-white/10 hover:bg-white/20'
                                                } ${bout.locked ? 'opacity-60' : ''}`}
                                                style={chosen ? { backgroundColor: accent } : undefined}>
                                            {busy === `${bout.id}:${f.side}`
                                                ? <Loader2 size={13} className="mx-auto animate-spin" />
                                                : <span>{f.name}{won ? ' ✓' : ''}</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        </li>
                    );
                })}
            </ul>
            {!block.signed_in && (
                <p className="mt-3 text-xs opacity-60" data-testid="goal-card-picks-signin">
                    {t('goalGame.cardPickSignIn')}
                </p>
            )}
        </div>
    );
};

export default CardPicks;
